import React, { useState, useEffect } from "react";
import classnames from "classnames";
import sortBy from "lodash.sortby";
import {ContextView, View} from "@instructure/ui-view";
import {Text} from "@instructure/ui-text";
import {IconInfoLine} from "@instructure/ui-icons";
import {IconButton} from "@instructure/ui-buttons";
import {TextInput} from "@instructure/ui-text-input";
import IasFormSelectInput from "../FormSelectInput/FormSelectInput";
import IasFormDateInput from "../FormDateInput/FormDateInput";
import IasFormActions from "../FormActions/FormActions";
import { useStateValue } from "../../context-store/AppStateProvider";
import useCreateCourse from "../../hooks/useCreateCourse";
import { ACTIONS } from "../../context-store/ACTIONS";
import { PROGRESS_ALERT_VARIANTS } from "../ProgressAlerts/ProgressAlerts";
import styles from "./FormCreateCourse.module.scss";
import { Grid } from "@instructure/ui-grid";
import { zonedTimeToUtc } from "date-fns-tz";
import { TimeZonesOptions } from "../../utils/timezonesOptions";
import timezoneMapping from "../../utils/tz.json";
import IasFormTimeInput from "../FormTimeInput/FormTimeInput";
import IasFormNumberInput from "../FormNumberInput/FormNumberInput";
import { Link } from "@instructure/ui-link";

const IasFormCreateCourse = ({
  dropdownOptions,
  dropdownDataError,
  userTimeZone,
  salesForceType,
  displayMinorLearners,
  maximumLearners
}) => {
  const [{ token, userId, maxDuration }, dispatch] = useStateValue();
  const [curriculumOptions, setCurriculumOptions] = useState([]);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [deliveryModalityOptions, setDeliveryModalityOptions] = useState([]);
  const [timeZoneDropdownValue, setTimeZoneDropdownValue] = useState("");
  const [apiLoading, setApiLoading] = useState(true);
  const [resetForm, setResetForm] = useState(false);
  const [resetSubDropdowns, setResetSubDropdowns] = useState(false);
  const timeZoneOptions = TimeZonesOptions;

  const initialFormValues = {
    curriculum: null,
    startDate: null,
    start_at: "08:00 AM",
    endDate: null,
    end_at: "06:00 PM",
    language: null,
    timezone: userTimeZone ? userTimeZone : "",
    deliveryModality: null,
    correctST: null,
    correctET: null,
    type: salesForceType,
    minorLearners: 0,
    courseNickname: ""
  };

  const initialFormValidation = {
    curriculum: false,
    startDate: false,
    endDate: false,
    language: false,
    deliveryModality: false,
  };

  const [formValues, setFormValues] = useState(initialFormValues);
  const [formValidation, setFormValidation] = useState(initialFormValidation);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);

  // API Calls here to build out dropdowns or classes
  useEffect(() => {
    // only curriculum has masterCurriculumNumber
    let curricula = dropdownOptions.map((o) => {
      return {
        id: o.id,
        title: o.curriculum,
        masterCurriculumNumber: o.masterCurriculumNumber,
      };
    });
    curricula = sortBy(curricula, ["title"]);
    setCurriculumOptions(curricula);
    setApiLoading(false);
  }, [dropdownOptions]);

  const buildOutLanguageModalityDropdowns = (selectedOption) => {
    const selectedCurriculum = selectedOption || formValues.curriculum;
    const subOptions = dropdownOptions.find(
      (o) => o.curriculum === selectedCurriculum
    );

    let languages = subOptions.language.map((l) => ({
      id: l.id,
      title: l.title,
    }));
    languages = sortBy(languages, ["title"]);

    let modalities = subOptions.deliveryModality.map((dm) => ({
      id: dm.id,
      title: dm.title,
    }));

    setLanguageOptions(languages);
    setDeliveryModalityOptions(modalities);
  };

  const associatedValue = Object.keys(timezoneMapping).find(
    (key) => timezoneMapping[key] === formValues.timezone
  );

  let selectedValue = timeZoneOptions.find(
    (timezone) => timezone.value === associatedValue
  );

  let changedTimezoneValue = timeZoneOptions.find(
    (timezone) => timezone.value === timeZoneDropdownValue.split("(")[0].trim()
  );

  const furthestDateForward = (startDate) => {
    const updatedDate = new Date(startDate);
    updatedDate.setDate(updatedDate.getDate() + parseInt(maxDuration));
    return updatedDate.toISOString();
  };

  const furthestDateBack = (startDate) => {
    const updatedDate = new Date(startDate);
    updatedDate.setDate(updatedDate.getDate() - parseInt(maxDuration));
    return updatedDate.toISOString();
  };

  // useEffect to switch on curriculum change to new dropdown filters for language and delivery modality
  useEffect(() => {
    if (curriculumOptions.length !== 0) {
      if (formValues.curriculum !== null) {
        buildOutLanguageModalityDropdowns();
      }
    }
    // eslint-disable-next-line
  }, [curriculumOptions, formValues]);

  // ** Internal form validation to enable submit button
  useEffect(() => {
    if (!Object.values(formValidation).every((value) => value === true)) {
      return setIsFormValid(false);
    }
    return setIsFormValid(true);
  }, [formValidation]);

  const handleSelectedOption = (name, option) => {
    if (!isFormDirty) {
      setIsFormDirty(true);
    }
    // handle resetting language and deliveryModality if curriculum is changed, reset language adn deliveryModality values
    if (name === "curriculum") {
      buildOutLanguageModalityDropdowns(option);
      setFormValues({
        ...formValues,
        language: null,
        deliveryModality: null,
        curriculum: option,
      });
      setFormValidation({
        ...formValidation,
        language: false,
        deliveryModality: false,
        curriculum: true,
      });
      // set resets for langauge and deliveryModality selects
      setResetSubDropdowns(true);
      setTimeout(() => {
        setResetSubDropdowns(false);
      });
      return;
    }
    setFormValues({ ...formValues, [`${name}`]: option });
    setFormValidation({ ...formValidation, [`${name}`]: true });

    if (name === "startTime" || name === "endTime") {
      if (name === "startTime") {
        setFormValues({
          ...formValues,
          start_at: option,
        });
      } else {
        setFormValues({
          ...formValues,
          end_at: option,
        });
      }
    }

    if (name === "timezone") {
      const timezoneKeyValue = timeZoneOptions.find(
        (timezoneOption) => timezoneOption.title === option
      );
      const timeZone = Object.keys(timezoneMapping).find(
        (key) => key === timezoneKeyValue.value
      );
      const updatedTimeZone = timezoneMapping[timeZone];

      setTimeZoneDropdownValue(option);
      setFormValues({
        ...formValues,
        timezone: updatedTimeZone,
      });
    }
  };

  // Listens for both onChange and onClick events and passes in to internal validation tracker
  // ** Inner FormDateInput component passes a valid date string back if can parse one else null
  const handleDate = (name, dateString) => {
    if (!isFormDirty) {
      setIsFormDirty(true);
    }

    if (dateString !== null) {
      if (name === "startDate") {
        const date = dateString.split("T")[0];
        setFormValues({ ...formValues, startDate: date });
        setFormValidation({ ...formValidation, [`${name}`]: true });
        return;
      } else if (name === "endDate") {
        const date = dateString.split("T")[0];
        setFormValues({ ...formValues, endDate: date });
        setFormValidation({ ...formValidation, [`${name}`]: true });
        return;
      }
    }
    setFormValidation({ ...formValidation, [`${name}`]: false });
    return;
  };

  const handleResetForm = () => {
    dispatch({
      type: ACTIONS.RESET_PROGRESS_ALERT,
    });
    setFormValidation(initialFormValidation);
    setFormValues(initialFormValues);
    setTimeZoneDropdownValue("");
    setIsFormDirty(false);
    setResetForm(true);
    setTimeout(() => {
      setResetForm(false);
    }, 0);
  };

  // Grab POST call calback from custom hook for use in handleFormSubmit
  const { postDataCallback } = useCreateCourse();

  const prepPayload = () => {
    const copiedFormValues = JSON.parse(JSON.stringify(formValues));

    if (copiedFormValues.minorLearners) {
      copiedFormValues.minorLearners = 0;
    }

    const selectedCurriculumObject = curriculumOptions.find(
      (c) => c.title === copiedFormValues.curriculum
    );

    // TimeZone Calculation
    const generateTime = (date, time, timezone) => {
      const [hours, duration] = time.split(" ");
      let [hour, minute] = hours.split(":");
      if (hour === "12" && duration === "AM") {
        hour = "00";
      } else if (hour === "12" && duration === "PM") {
        hour = "12";
      } else if (duration === "PM" && hours !== "12") {
        hour = +hour + 12;
      }

      const updatedTime = `${hour}:${minute}:00`;

      let updatedDate = date + " " + updatedTime;
      updatedDate = zonedTimeToUtc(updatedDate, timezone).toISOString();
      return updatedDate;
    };

    const updatedStart = generateTime(
      copiedFormValues.startDate,
      copiedFormValues.start_at,
      copiedFormValues.timezone
    );
    const updatedEnd = generateTime(
      copiedFormValues.endDate,
      copiedFormValues.end_at,
      copiedFormValues.timezone
    );

    let selectedTimeZone;
    let updatedTimeZone;
    if (timeZoneDropdownValue === "") {
      selectedTimeZone = Object.keys(timezoneMapping).find(
        (timezone) => timezoneMapping[timezone] === initialFormValues.timezone
      );
      updatedTimeZone = { id: selectedTimeZone };
    } else {
      selectedTimeZone = timeZoneDropdownValue.split("(")[0].trim();
      updatedTimeZone = timeZoneOptions.find((tzOptions) =>
        tzOptions.id.includes(selectedTimeZone)
      );
    }


    const preppedPayload = {
      ...copiedFormValues,
      startDate: updatedStart,
      endDate: updatedEnd,
      timezone: updatedTimeZone.id,
      start_at: null,
      end_at: null,
      correctST: null,
      correctET: null,
    };

    if (process.env.NODE_ENV === "production") {
      preppedPayload.id = selectedCurriculumObject.id;
    }

    preppedPayload.masterCurriculumNumber =
      selectedCurriculumObject.masterCurriculumNumber;

    preppedPayload.language_id = languageOptions.find(
      (l) => l.title === copiedFormValues.language
    ).id;

    preppedPayload.courseNickname = copiedFormValues.courseNickname;
    return preppedPayload;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    dispatch({
      type: ACTIONS.SET_IS_CALLING_API,
      isCallingApi: true,
    });

    if (!isFormValid) {
      console.error("There was an error validating your form");
      dispatch({
        type: ACTIONS.SET_IS_CALLING_API,
        isCallingApi: false,
      });
      return dispatch({
        type: ACTIONS.SET_PROGRESS_ALERT,
        progressAlert: {
          variant: PROGRESS_ALERT_VARIANTS.ERROR,
          message: `There was an error validating your form. Contact admins if this error persists.`,
        },
      });
    }

    const deferredLearnerCount = formValues.minorLearners;

    let postPayload = prepPayload();

    const { response, error } = await postDataCallback(postPayload);

    // POST API error
    if (error !== null) {
      setTimeout(() => {
        dispatch({
          type: ACTIONS.SET_IS_CALLING_API,
          isCallingApi: false,
        });
        console.error(`POST call got following error: ${error}`);
        dispatch({
          type: ACTIONS.SET_PROGRESS_ALERT,
          progressAlert: {
            variant: PROGRESS_ALERT_VARIANTS.ERROR,
            message: error.message
              ? error.message
              : "There was an error validating your form. Contact admins if this error persists.",
          },
        });
      }, 1000);
    }

    // POST API success
    if (response !== null) {
      if (deferredLearnerCount > 0) {
        const queryParams = [];
        queryParams.push("courseId="+response.canvasCourseId);
        queryParams.push("userId="+userId);
        queryParams.push("addedLearnerCount="+deferredLearnerCount);

        const url = `/api/addMinorLearner?${queryParams.join('&')}`;

        fetch(url, {
          headers: {
              'Content-Type': 'application/json',
              'Authorization': 'Bearer ' + token
          },
          method: "PUT",
          credentials: 'same-origin'
        }).catch();
      }

      setTimeout(() => {
        dispatch({
          type: ACTIONS.SET_IS_CALLING_API,
          isCallingApi: false,
        });

        handleResetForm();

        const packageForModal = JSON.parse(JSON.stringify(response));

        // TODO - remove for final release
        if (process.env.NODE_ENV === "development") {
          const randomSuccessError = !!Math.round(Math.random());
          packageForModal.title = randomSuccessError
            ? "Test Title Request Processing..."
            : "Test Error Creating Canvas Course";
          packageForModal.message = randomSuccessError
            ? "Your request is being processed. You'll receive a Canvas notification when the course has been successfully created"
            : "There was an error with Canvas course creation. Please contact your administrator.";
          packageForModal.error = !randomSuccessError;
        }
        // TODO - remove above for final release

        dispatch({
          type: ACTIONS.SHOW_CREATED_COURSE_MODAL,
          createdCourse: packageForModal,
        });
      }, 1000);
    }
  };

  return (
    <View as="section" padding="none medium medium">
      <form
        className={classnames(styles.FormCreateCourse)}
        onSubmit={handleFormSubmit}
      >
        <Grid colSpacing="small" rowSpacing="small">
          <Grid.Row hAlign="space-between">
            <Grid.Col width={{ small: 5.5, medium: 5.5, large: 5.5, xLarge: 5.5 }}>
              <IasFormSelectInput
                id="curriculum"
                name="curriculum"
                label={"Course"}
                reset={resetForm}
                placeholder={"Select a course..."}
                isRequired={true}
                isDisabled={dropdownDataError}
                onSelected={handleSelectedOption}
                isLoading={apiLoading}
                options={curriculumOptions}
              />
            </Grid.Col>
            <Grid.Col width={{ small: 5.5, medium: 5.5, large: 5.5, xLarge: 5.5 }}>
              <TextInput
                renderLabel="Course Nickname"
                id="courseNickname"
                maxLength="59"
                placeholder="Enter Course Nickname"
                messages={[{type: "hint", text: "This is not a required field, a course nickname is only visible to you to easily identify your courses."}]}
                onChange={(event, value) => {
                  setFormValues({
                    ...formValues,
                    courseNickname: value,
                  }) 
                }}
              />
            </Grid.Col>
          </Grid.Row>
          <Grid.Row hAlign="space-between">
            <Grid.Col width={{ small: 5.5, medium: 5.5, large: 5.5, xLarge: 5.5 }}>
              <IasFormDateInput
                id="startDate"
                name="startDate"
                label="Start Date"
                placeholder="Choose a Start Date..."
                size="medium"
                timezone={formValues.timezone}
                minDate={
                  formValues.endDate
                    ? furthestDateBack(formValues.endDate)
                    : null
                }
                maxDate={formValues.endDate}
                dateErrorMessage={"Start date must before End Date"}
                isRequired={true}
                minDateErrorMessage={
                  "Error: Class duration must be no more than " + maxDuration + " days."
                }
                isDisabled={dropdownDataError}
                reset={resetForm}
                onDateChange={handleDate}
                onDateBlur={handleDate}
                onDateSelected={handleDate}
                isLoading={apiLoading}
              />
            </Grid.Col>
            <Grid.Col width={{ small: 5.5, medium: 5.5, large: 5.5, xLarge: 5.5 }}>
              <IasFormTimeInput
                id="start_at"
                name="startTime"
                renderLabel="Start Time"
                value={formValues.start_at}
                step={30}
                reset={resetForm}
                onChange={(_, value, option) => {
                  handleSelectedOption("startTime", value);
                }}
                dropdownDataError={dropdownDataError}
                isLoading={apiLoading}
              />
            </Grid.Col>
          </Grid.Row>
        </Grid>
        <Grid colSpacing="small" rowSpacing="small">
          <Grid.Row hAlign="space-between">
            <Grid.Col width={{ small: 5.5, medium: 5.5, large: 5.5, xLarge: 5.5 }}>
              <IasFormDateInput
                id="endDate"
                name="endDate"
                label="End Date"
                placeholder="Choose an End Date..."
                size="medium"
                timezone={formValues.timezone}
                minDate={formValues.startDate}
                maxDate={
                  formValues.startDate
                    ? furthestDateForward(formValues.startDate)
                    : null
                }
                messages={[
                  { type: "hint", text: "Class duration must be no more than " + maxDuration + " days." },
                ]}
                dateErrorMessage={"End date must be after Start Date"}
                isRequired={true}
                maxDateErrorMessage={
                  "Error: Class duration must be no more than " + maxDuration + " days."
                }
                isDisabled={dropdownDataError}
                reset={resetForm}
                onDateChange={handleDate}
                onDateBlur={handleDate}
                onDateSelected={handleDate}
                isLoading={apiLoading}
              />
            </Grid.Col>
            <Grid.Col width={{ small: 5.5, medium: 5.5, large: 5.5, xLarge: 5.5 }}>
              <IasFormTimeInput
                id="end_at"
                name="endTime"
                renderLabel="End Time"
                value={formValues.end_at}
                reset={resetForm}
                onChange={(_, value, option) => {
                  handleSelectedOption("endTime", value);
                }}
                dropdownDataError={dropdownDataError}
                isLoading={apiLoading}
              />
            </Grid.Col>
          </Grid.Row>
        </Grid>

        <IasFormSelectInput
          id="timezone"
          name="timezone"
          label={"Time Zone"}
          reset={resetForm || resetSubDropdowns}
          placeholder={"Select a time zone..."}
          isRequired={true}
          selected={changedTimezoneValue ? changedTimezoneValue : selectedValue}
          isDisabled={dropdownDataError}
          onSelected={handleSelectedOption}
          isLoading={apiLoading}
          options={timeZoneOptions}
        />

        <IasFormSelectInput
          id="language"
          name="language"
          label={"Language"}
          reset={resetForm || resetSubDropdowns}
          placeholder={"Select a language..."}
          isRequired={true}
          isDisabled={dropdownDataError || formValues.curriculum === null}
          onSelected={handleSelectedOption}
          isLoading={apiLoading}
          options={languageOptions}
        />
        <IasFormSelectInput
          id="deliveryModality"
          name="deliveryModality"
          label={"Delivery Modality"}
          reset={resetForm || resetSubDropdowns}
          placeholder={"Select a delivery modality..."}
          isRequired={true}
          messages={[
            {
              type: "hint",
              text: "Delivery Modality is requested for reporting purposes only.",
            },
          ]}
          size="medium"
          isDisabled={dropdownDataError || formValues.curriculum === null}
          onSelected={handleSelectedOption}
          isLoading={apiLoading}
          options={deliveryModalityOptions}
        />

        { displayMinorLearners ?
          <IasFormNumberInput
            id="minorLearners"
            name="minorLearners"
            label={"Number of Learners"}
            reset={resetForm || resetSubDropdowns}
            placeholder={"Select the amount required"}
            isRequired={false}
            messages={[
              {
                type: "hint",
                text: `You can add a maximum of ${maximumLearners} students.`,
              },
            ]}
            size="medium"
            isDisabled={dropdownDataError || formValues.curriculum === null}
            onChange={handleSelectedOption}
            isLoading={apiLoading}
            maxValue={maximumLearners}
            minValue={0}
          /> : null
        }

        <View
            as="div"
            margin="medium"
            padding="medium"
            background="light"
            borderWidth="small"
            shadow="above"
        >
          <View>
            <Text weight="bold" size="medium" lineHeight="double">
              <IconInfoLine size="x-small"/>&nbsp; Acceptable Use Policy
            </Text>
          </View>
          <Text>&nbsp;</Text>
          <Text as="div" weight="normal" wrap="break-word" size="small" margin="medium">
            Your use of AWS {initialFormValues.type} is governed by the AWS <Link size="medium" href="https://aws.amazon.com/aup/" target="_blank">Acceptable Use Policy</Link> (“the Policy”).  AWS {initialFormValues.type} may audit its services for any violations
            of the Policy and remove or disable your access in response to any violations.
          </Text>
        </View>

        <IasFormActions
          isLoading={apiLoading}
          onReset={handleResetForm}
          disableReset={!isFormDirty}
          disableSubmit={!isFormValid}
        />
      </form>
    </View>
  );
};

export default IasFormCreateCourse;
